import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Save, Send, X, UserPlus, FilePlus2 } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import { FormSection, FormField, StickyActionBar } from "@/components/common/FormSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import AttachmentsPanel from "@/pages/onboarding/AttachmentsPanel";

const EMPTY = {
  patient: { mrn: "", first_name: "", last_name: "", national_id: "", dob: "", gender: "", nationality: "", phone: "", email: "", address: "", city: "" },
  referral: { source: "", facility_id: "", department: "", referring_physician: "", referral_date: "" },
  clinical: { primary_diagnosis: "", secondary_diagnosis: "", clinical_summary: "", current_medications: "", allergies: "", clinical_notes: "" },
  service: { requested_service: "", priority: "routine", expected_start_date: "" },
};

function genMrn() {
  return `MRN-${Math.floor(100000 + Math.random() * 900000)}`;
}

export default function CreateRequest({ mode = "new" }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = mode === "edit" || !!id;

  const [form, setForm] = useState(EMPTY);
  const [request, setRequest] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [refSources, setRefSources] = useState([]);
  const [refDepartments, setRefDepartments] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [services, setServices] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [existingPatients, setExistingPatients] = useState([]);
  const [patientQuery, setPatientQuery] = useState("");

  useEffect(() => {
    async function loadLookups() {
      try {
        const cats = await api.get("/lookups/categories", { params: { page_size: 200 } });
        const findCat = (code) => cats.data.items.find((c) => c.code === code);
        const fetchValues = async (code) => {
          const cat = findCat(code);
          if (!cat) return [];
          const v = await api.get("/lookups/values", { params: { category_id: cat.id, page_size: 200 } });
          return v.data.items;
        };
        const [src, dep, pri, svc] = await Promise.all([
          fetchValues("referral_sources"), fetchValues("referral_departments"),
          fetchValues("referral_priorities"), fetchValues("requested_services"),
        ]);
        setRefSources(src); setRefDepartments(dep); setPriorities(pri); setServices(svc);
        const fac = await api.get("/facilities", { params: { page_size: 100 } });
        setFacilities(fac.data.items);
      } catch (_e) {}
    }
    loadLookups();
  }, []);

  useEffect(() => {
    if (!id) {
      setForm({ ...EMPTY, patient: { ...EMPTY.patient, mrn: genMrn() } });
      return;
    }
    api.get(`/onboarding/requests/${id}`).then((r) => {
      setRequest(r.data);
      setForm({
        patient: r.data.patient || EMPTY.patient,
        referral: r.data.referral || EMPTY.referral,
        clinical: r.data.clinical || EMPTY.clinical,
        service: r.data.service || EMPTY.service,
      });
    }).catch((e) => toast.error(formatApiError(e)));
  }, [id]);

  useEffect(() => {
    if (patientQuery.length < 2) { setExistingPatients([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await api.get("/patients", { params: { q: patientQuery, page_size: 8 } });
        setExistingPatients(res.data.items);
      } catch (_e) { setExistingPatients([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [patientQuery]);

  function setPatientFromExisting(p) {
    setForm((f) => ({
      ...f,
      patient: {
        ...f.patient,
        patient_id: p.id,
        mrn: p.mrn, first_name: p.first_name, last_name: p.last_name,
        dob: p.dob || "", gender: p.gender || "", phone: p.phone || "", email: p.email || "",
        address: p.address || "", city: p.city || "",
      },
    }));
    setExistingPatients([]);
    setPatientQuery("");
    toast.success(`Patient ${p.first_name} ${p.last_name} loaded`);
  }

  function update(section, field, value) {
    setForm((f) => ({ ...f, [section]: { ...f[section], [field]: value } }));
  }

  function validate() {
    const e = {};
    if (!form.patient.mrn) e["patient.mrn"] = "MRN is required";
    if (!form.patient.first_name) e["patient.first_name"] = "Required";
    if (!form.patient.last_name) e["patient.last_name"] = "Required";
    if (!form.clinical.primary_diagnosis) e["clinical.primary_diagnosis"] = "Primary diagnosis is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function save(submit = false) {
    if (!validate()) { toast.error("Please fix validation errors."); return; }
    setSaving(true);
    try {
      let req = request;
      if (isEdit && req) {
        const res = await api.put(`/onboarding/requests/${req.id}`, form);
        req = res.data;
      } else {
        const res = await api.post("/onboarding/requests", form);
        req = res.data;
        setRequest(req);
        // Update URL so attachments can be tied to this request id
        window.history.replaceState(null, "", `/onboarding/requests/${req.id}/edit`);
      }
      if (submit) {
        const res2 = await api.post(`/onboarding/requests/${req.id}/submit`, {});
        req = res2.data;
        toast.success("Request submitted for medical review");
        navigate(`/onboarding/requests/${req.id}`);
        return;
      }
      toast.success("Draft saved");
      setRequest(req);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  const isResubmit = request?.status === "information_requested";

  return (
    <div data-testid="ob-create-page">
      <PageHeader
        title={isEdit ? `Edit Request ${request?.ref_number || ""}` : "Create Onboarding Request"}
        subtitle={isResubmit
          ? "Address the information requested by the physician and resubmit."
          : "Initiate a Hospital @ Home referral. Save as draft or submit for medical review."}
        actions={
          <Button variant="outline" onClick={() => navigate(-1)} className="border-[#E5E7EB]" data-testid="ob-cancel-create">
            <X className="w-4 h-4 mr-1.5" /> Cancel
          </Button>
        }
      />

      {isResubmit && request?.information_requested && (
        <div className="mb-4 p-4 rounded-lg bg-[#F59E0B14] border border-[#F59E0B33] text-[#92400E] text-sm" data-testid="ob-info-request-banner">
          <div className="font-semibold mb-1">Physician requested information</div>
          <div>{request.information_requested}</div>
          {request.information_request_notes && (
            <div className="text-xs mt-1 opacity-80">{request.information_request_notes}</div>
          )}
        </div>
      )}

      <div className="space-y-4">
        {/* Patient lookup */}
        {!isEdit && (
          <div className="hah-card p-5">
            <div className="hah-section-title mb-1">Patient lookup</div>
            <h3 className="text-base font-semibold text-[#1F2937]">Search existing patient or create new</h3>
            <div className="mt-3 relative">
              <Input
                data-testid="ob-patient-search"
                placeholder="Search by name or MRN…"
                value={patientQuery}
                onChange={(e) => setPatientQuery(e.target.value)}
              />
              {existingPatients.length > 0 && (
                <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-md max-h-72 overflow-y-auto">
                  {existingPatients.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setPatientFromExisting(p)}
                      data-testid={`ob-patient-pick-${p.id}`}
                      className="w-full text-left px-3 py-2 hover:bg-[#EAF8FA]/50 flex items-center gap-3 border-b border-[#E5E7EB] last:border-b-0"
                    >
                      <div className="w-8 h-8 rounded-full bg-[#EAF8FA] text-[#0B7E8C] flex items-center justify-center text-xs font-semibold">
                        {(p.first_name?.[0] || "?") + (p.last_name?.[0] || "")}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-[#1F2937] truncate">{p.first_name} {p.last_name}</div>
                        <div className="text-xs text-[#6B7280]">{p.mrn} · DOB {p.dob || "—"}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Patient info */}
        <FormSection title="Patient Information" description="Identity and contact details of the patient." testId="ob-section-patient">
          <FormField label="MRN" required error={errors["patient.mrn"]}>
            <Input data-testid="ob-patient-mrn" value={form.patient.mrn} onChange={(e) => update("patient", "mrn", e.target.value)} />
          </FormField>
          <FormField label="National ID / Iqama">
            <Input data-testid="ob-patient-nid" value={form.patient.national_id || ""} onChange={(e) => update("patient", "national_id", e.target.value)} />
          </FormField>
          <FormField label="First name" required error={errors["patient.first_name"]}>
            <Input data-testid="ob-patient-first" value={form.patient.first_name} onChange={(e) => update("patient", "first_name", e.target.value)} />
          </FormField>
          <FormField label="Last name" required error={errors["patient.last_name"]}>
            <Input data-testid="ob-patient-last" value={form.patient.last_name} onChange={(e) => update("patient", "last_name", e.target.value)} />
          </FormField>
          <FormField label="Date of birth">
            <Input type="date" value={form.patient.dob || ""} onChange={(e) => update("patient", "dob", e.target.value)} />
          </FormField>
          <FormField label="Gender">
            <Select value={form.patient.gender || ""} onValueChange={(v) => update("patient", "gender", v)}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Nationality">
            <Input value={form.patient.nationality || ""} onChange={(e) => update("patient", "nationality", e.target.value)} />
          </FormField>
          <FormField label="Mobile number">
            <Input value={form.patient.phone || ""} onChange={(e) => update("patient", "phone", e.target.value)} />
          </FormField>
          <FormField label="Email">
            <Input type="email" value={form.patient.email || ""} onChange={(e) => update("patient", "email", e.target.value)} />
          </FormField>
          <FormField label="Address" full>
            <Input value={form.patient.address || ""} onChange={(e) => update("patient", "address", e.target.value)} />
          </FormField>
          <FormField label="City">
            <Input value={form.patient.city || ""} onChange={(e) => update("patient", "city", e.target.value)} />
          </FormField>
        </FormSection>

        {/* Referral */}
        <FormSection title="Referral Information" testId="ob-section-referral" description="Who is referring this patient.">
          <FormField label="Referral source">
            <Select value={form.referral.source || ""} onValueChange={(v) => update("referral", "source", v)}>
              <SelectTrigger data-testid="ob-ref-source"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {refSources.map((s) => <SelectItem key={s.id} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Referral facility">
            <Select value={form.referral.facility_id || ""} onValueChange={(v) => update("referral", "facility_id", v)}>
              <SelectTrigger><SelectValue placeholder="Select facility" /></SelectTrigger>
              <SelectContent>
                {facilities.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Department">
            <Select value={form.referral.department || ""} onValueChange={(v) => update("referral", "department", v)}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {refDepartments.map((d) => <SelectItem key={d.id} value={d.value}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Referring physician">
            <Input value={form.referral.referring_physician || ""} onChange={(e) => update("referral", "referring_physician", e.target.value)} />
          </FormField>
          <FormField label="Referral date">
            <Input type="date" value={form.referral.referral_date || ""} onChange={(e) => update("referral", "referral_date", e.target.value)} />
          </FormField>
        </FormSection>

        {/* Clinical */}
        <FormSection title="Clinical Information" testId="ob-section-clinical" description="Diagnoses, history and clinical context.">
          <FormField label="Primary diagnosis" required error={errors["clinical.primary_diagnosis"]} full>
            <Input data-testid="ob-clinical-primary" value={form.clinical.primary_diagnosis} onChange={(e) => update("clinical", "primary_diagnosis", e.target.value)} />
          </FormField>
          <FormField label="Secondary diagnosis" full>
            <Input value={form.clinical.secondary_diagnosis || ""} onChange={(e) => update("clinical", "secondary_diagnosis", e.target.value)} />
          </FormField>
          <FormField label="Clinical summary" full>
            <Textarea rows={3} value={form.clinical.clinical_summary || ""} onChange={(e) => update("clinical", "clinical_summary", e.target.value)} />
          </FormField>
          <FormField label="Current medications" full>
            <Textarea rows={2} value={form.clinical.current_medications || ""} onChange={(e) => update("clinical", "current_medications", e.target.value)} />
          </FormField>
          <FormField label="Allergies">
            <Input value={form.clinical.allergies || ""} onChange={(e) => update("clinical", "allergies", e.target.value)} />
          </FormField>
          <FormField label="Clinical notes" full>
            <Textarea rows={2} value={form.clinical.clinical_notes || ""} onChange={(e) => update("clinical", "clinical_notes", e.target.value)} />
          </FormField>
        </FormSection>

        {/* Service */}
        <FormSection title="Service Information" testId="ob-section-service" description="What service is being requested.">
          <FormField label="Requested service">
            <Select value={form.service.requested_service || ""} onValueChange={(v) => update("service", "requested_service", v)}>
              <SelectTrigger data-testid="ob-service"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {services.map((s) => <SelectItem key={s.id} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Priority">
            <Select value={form.service.priority || "routine"} onValueChange={(v) => update("service", "priority", v)}>
              <SelectTrigger data-testid="ob-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                {priorities.length > 0 ? priorities.map((p) => (
                  <SelectItem key={p.id} value={p.value}>{p.label}</SelectItem>
                )) : (
                  <>
                    <SelectItem value="routine">Routine</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="stat">STAT</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Expected start date">
            <Input type="date" value={form.service.expected_start_date || ""} onChange={(e) => update("service", "expected_start_date", e.target.value)} />
          </FormField>
        </FormSection>

        {/* Attachments (only when request exists / has id) */}
        {request?.id && (
          <AttachmentsPanel requestId={request.id} />
        )}
        {!request?.id && (
          <div className="hah-card p-5 text-sm text-[#6B7280]" data-testid="ob-attachments-hint">
            Save the request as a draft first to enable attachments (referral form, clinical documents, lab/radiology reports).
          </div>
        )}
      </div>

      <StickyActionBar testId="ob-create-actions">
        <Button variant="outline" onClick={() => navigate("/onboarding")} className="border-[#E5E7EB]" data-testid="ob-cancel">
          Cancel
        </Button>
        <Button onClick={() => save(false)} disabled={saving} variant="outline" className="border-[#0B7E8C] text-[#0B7E8C] hover:bg-[#EAF8FA]" data-testid="ob-save-draft">
          <Save className="w-4 h-4 mr-1.5" /> Save Draft
        </Button>
        <Button onClick={() => save(true)} disabled={saving} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="ob-submit">
          <Send className="w-4 h-4 mr-1.5" /> {isResubmit ? "Resubmit" : "Submit for Review"}
        </Button>
      </StickyActionBar>
    </div>
  );
}
